import { db } from './config';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where
} from 'firebase/firestore';

const COLLECTION_NAME = 'services';
const TREATMENTS_COLLECTION = 'treatments';

const DEFAULT_AVAILABLE_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DEFAULT_AVAILABLE_TIMES = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM'
];

const buildTreatmentPayload = (serviceData, serviceId, existingTreatment = null) => ({
  name: serviceData.title,
  description: serviceData.description || '',
  availability: existingTreatment?.availability || 'Monday to Friday, 9:00 AM to 5:00 PM',
  availableDays: existingTreatment?.availableDays || DEFAULT_AVAILABLE_DAYS,
  availableTimes: existingTreatment?.availableTimes || DEFAULT_AVAILABLE_TIMES,
  active: existingTreatment?.active !== undefined ? existingTreatment.active : true,
  linkedServiceId: serviceId,
  updatedAt: new Date().toISOString()
});

const findTreatmentForService = async (serviceId, serviceTitle) => {
  const treatmentsRef = collection(db, TREATMENTS_COLLECTION);

  if (serviceId) {
    const linkedQuery = query(treatmentsRef, where('linkedServiceId', '==', serviceId));
    const linkedSnap = await getDocs(linkedQuery);
    if (!linkedSnap.empty) {
      const docSnap = linkedSnap.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
  }

  if (serviceTitle) {
    const allSnap = await getDocs(treatmentsRef);
    const match = allSnap.docs.find((d) => {
      const data = d.data();
      return (data.name || '').trim().toLowerCase() === serviceTitle.trim().toLowerCase();
    });
    if (match) {
      return { id: match.id, ...match.data() };
    }
  }

  return null;
};

const syncTreatmentForService = async (serviceData, serviceId) => {
  try {
    const existing = await findTreatmentForService(serviceId, serviceData.title);
    const payload = buildTreatmentPayload(serviceData, serviceId, existing);

    if (existing) {
      const treatmentRef = doc(db, TREATMENTS_COLLECTION, existing.id);
      await updateDoc(treatmentRef, payload);
      return { id: existing.id, ...payload };
    }

    const created = {
      ...payload,
      createdAt: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, TREATMENTS_COLLECTION), created);
    return { id: docRef.id, ...created };
  } catch (error) {
    console.error('Error syncing treatment for service:', error);
    return null;
  }
};

const removeTreatmentForService = async (serviceId, serviceTitle) => {
  try {
    const existing = await findTreatmentForService(serviceId, serviceTitle);
    if (existing) {
      await deleteDoc(doc(db, TREATMENTS_COLLECTION, existing.id));
    }
  } catch (error) {
    console.error('Error removing linked treatment:', error);
  }
};

export const getServices = async () => {
  try {
    const servicesCollection = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(servicesCollection);

    const services = [];
    querySnapshot.forEach((docSnap) => {
      services.push({ id: docSnap.id, ...docSnap.data() });
    });

    return services.sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 999;
      const orderB = b.order !== undefined ? b.order : 999;
      if (orderA === orderB) {
        return (a.title || '').localeCompare(b.title || '');
      }
      return orderA - orderB;
    });
  } catch (error) {
    console.error('Error getting services:', error);
    return [];
  }
};

export const addService = async (serviceData) => {
  try {
    let imageUrl = serviceData.image;

    if (!imageUrl || imageUrl === '') {
      imageUrl = '/images/placeholder-service.svg';
    }

    const newService = {
      ...serviceData,
      image: imageUrl,
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), newService);

    await syncTreatmentForService(newService, docRef.id);

    return { id: docRef.id, ...newService };
  } catch (error) {
    console.error('Error adding service:', error);
    throw error;
  }
};

export const updateService = async (id, serviceData) => {
  try {
    let imageUrl = serviceData.image;

    if (!imageUrl || imageUrl === '') {
      imageUrl = serviceData.originalImage || '/images/placeholder-service.svg';
    }

    const { originalImage, ...cleanServiceData } = serviceData;

    const updatedData = {
      ...cleanServiceData,
      image: imageUrl,
      updatedAt: new Date().toISOString()
    };

    const serviceRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(serviceRef, updatedData);

    await syncTreatmentForService(updatedData, id);

    return { id, ...updatedData };
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

export const deleteService = async (id) => {
  try {
    const servicesRef = collection(db, COLLECTION_NAME);
    const allSnap = await getDocs(servicesRef);
    const target = allSnap.docs.find((d) => d.id === id);
    const targetTitle = target ? target.data().title : null;

    const serviceRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(serviceRef);

    await removeTreatmentForService(id, targetTitle);

    return id;
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

export const cleanupDuplicateServices = async () => {
  try {
    const servicesCollection = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(servicesCollection);

    const allServices = [];
    querySnapshot.forEach((docSnap) => {
      allServices.push({ id: docSnap.id, ...docSnap.data() });
    });

    const seen = new Map();
    const duplicates = [];

    allServices
      .slice()
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aTime - bTime;
      })
      .forEach((service) => {
        const key = (service.title || '').trim().toLowerCase();
        if (!key) return;
        if (seen.has(key)) {
          duplicates.push(service);
        } else {
          seen.set(key, service);
        }
      });

    for (const dup of duplicates) {
      await deleteDoc(doc(db, COLLECTION_NAME, dup.id));
    }

    return { removed: duplicates.length, kept: seen.size };
  } catch (error) {
    console.error('Error cleaning up duplicate services:', error);
    throw error;
  }
};

export const syncAllServicesToTreatments = async () => {
  try {
    const servicesCollection = collection(db, COLLECTION_NAME);
    const servicesSnap = await getDocs(servicesCollection);

    const services = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    let created = 0;
    let updated = 0;

    for (const service of services) {
      const existing = await findTreatmentForService(service.id, service.title);
      const payload = buildTreatmentPayload(service, service.id, existing);

      if (existing) {
        const treatmentRef = doc(db, TREATMENTS_COLLECTION, existing.id);
        await updateDoc(treatmentRef, payload);
        updated += 1;
      } else {
        await addDoc(collection(db, TREATMENTS_COLLECTION), {
          ...payload,
          createdAt: new Date().toISOString()
        });
        created += 1;
      }
    }

    return { created, updated, total: services.length };
  } catch (error) {
    console.error('Error syncing all services to treatments:', error);
    throw error;
  }
};
